import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { getType, getColor, SIZES, plushFilter, saveTrophy } from "../prizes.js";
import Model3D from "../Model3D.jsx";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";

/* ==================== HANGTIME – the tightrope walker =========================
   Keep the man on the rope! An inverted pendulum: the needle at the top shows
   his lean — keep it inside the green zone or he tumbles into the net.
   DIFFERENT controls per device (deliberately different gameplay):
   - Desktop: the walker leans towards your MOUSE — smooth analog control.
   - Phone + gyro: TILT the phone to lean (iOS asks permission first).
   - Phone without gyro: hold left/right half of the screen.
   Difficulty (instability + wind gusts) ramps with time; prize by seconds.
================================================================================ */

const W = 360, H = 540;
const ROPE_Y = 300;
const FALL_ANG = 0.6;            // radians: beyond this he falls
const U_MAX = 5.2;               // max corrective acceleration
const PRIZE = { lille: 20, mellem: 35, stor: 50, pokal: 70 };  // seconds

/* linedanseren er maskotten (teddy_standing-spriten, A-pose) */
const teddyImg = typeof Image !== "undefined" ? new Image() : null;
if (teddyImg) teddyImg.src = "/images/hangtime-teddy.png";

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    gust() { const c = ensure(); if (!c) return; const o = c.createOscillator(), g = c.createGain(); o.type = "sawtooth"; o.frequency.setValueAtTime(90, c.currentTime); o.frequency.exponentialRampToValueAtTime(240, c.currentTime + 0.5); g.gain.setValueAtTime(0.001, c.currentTime); g.gain.exponentialRampToValueAtTime(0.05, c.currentTime + 0.2); g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.65); o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.7); },
    warn() { const c = ensure(); if (c) tone(740, c.currentTime, 0.08, "square", 0.07); },
    cross() { const c = ensure(); if (!c) return; [660, 880].forEach((f, i) => tone(f, c.currentTime + i * 0.08, 0.12, "triangle", 0.1)); },
    fall() { const c = ensure(); if (!c) return; const o = c.createOscillator(), g = c.createGain(); o.type = "sine"; o.frequency.setValueAtTime(900, c.currentTime); o.frequency.exponentialRampToValueAtTime(180, c.currentTime + 0.7); g.gain.setValueAtTime(0.12, c.currentTime); g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.75); o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.75); },
    boing() { const c = ensure(); if (c) tone(120, c.currentTime, 0.3, "triangle", 0.14); },
    win() { const c = ensure(); if (!c) return; [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14)); },
  };
}

const prizeFor = (s) =>
  s >= PRIZE.pokal ? "pokal" : s >= PRIZE.stor ? "stor" : s >= PRIZE.mellem ? "mellem" : s >= PRIZE.lille ? "lille" : null;

const isTouchDevice = () => typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
const hasFinePointer = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer:fine)").matches;

export default function Hangtime({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | done
  const [mode, setMode] = useState("mouse");     // mouse | gyro | touch
  const [hud, setHud] = useState({ time: 0, crossings: 0 });
  const [finalTime, setFinalTime] = useState(0);
  const [claimed, setClaimed] = useState(null);
  const [gyroErr, setGyroErr] = useState(false);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const touch = isTouchDevice();

  const startGame = (m) => {
    setMode(m);
    setClaimed(null);
    setFinalTime(0);
    setHud({ time: 0, crossings: 0 });
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* gyro needs a user-gesture permission request on iOS 13+ */
  const startGyro = async () => {
    try {
      if (typeof DeviceOrientationEvent !== "undefined" &&
          typeof DeviceOrientationEvent.requestPermission === "function") {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") { setGyroErr(true); return; }
      }
      startGame("gyro");
    } catch {
      setGyroErr(true);
    }
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const audio = audioRef.current;

    const g = {
      t: 0, over: false,
      ang: (Math.random() - 0.5) * 0.1, angV: 0,
      x: 70, dir: 1, crossings: 0,
      input: 0,                 // -1..1 corrective lean (pointer/gyro)
      keyInput: 0,              // arrow keys override while held
      wind: 0, gustT: 1.6, gustDur: 0, gustAmp: 0,
      fallY: 0, fallV: 0, netT: 0,
      walk: 0,                  // gang-fase: styrer dut/vug og linens svaj
      warnT: 0, popups: [],
    };
    if (localStorage.getItem("carnival.debug")) window.__ht = g;

    /* ---- sizing ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- input per mode ---- */
    const onMouseMove = (e) => {
      const r = canvas.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;   // -1..1 across canvas
      g.input = Math.max(-1, Math.min(1, nx * 1.4));
    };
    const onOrient = (e) => {
      if (e.gamma == null) return;
      g.input = Math.max(-1, Math.min(1, e.gamma / 22));     // tilt phone = lean
    };
    let touchSide = 0;
    const tStart = (e) => {
      const r = canvas.getBoundingClientRect();
      const x = e.touches[0].clientX - r.left;
      touchSide = x < r.width / 2 ? -1 : 1;
      g.input = touchSide * 0.85;
    };
    const tMove = (e) => {
      const r = canvas.getBoundingClientRect();
      const x = e.touches[0].clientX - r.left;
      touchSide = x < r.width / 2 ? -1 : 1;
      g.input = touchSide * 0.85;
    };
    const tEnd = () => { touchSide = 0; g.input = 0; };

    if (mode === "mouse") window.addEventListener("mousemove", onMouseMove);
    if (mode === "gyro") window.addEventListener("deviceorientation", onOrient);
    if (mode === "touch") {
      canvas.addEventListener("touchstart", tStart, { passive: true });
      canvas.addEventListener("touchmove", tMove, { passive: true });
      canvas.addEventListener("touchend", tEnd);
    }
    // arrow keys work in every mode (desktop) and OVERRIDE the pointer while held
    const keys = { l: false, r: false };
    const syncKeys = () => { g.keyInput = (keys.r ? 1 : 0) - (keys.l ? 1 : 0); };
    const kDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") { keys.l = true; syncKeys(); }
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") { keys.r = true; syncKeys(); }
    };
    const kUp = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") { keys.l = false; syncKeys(); }
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") { keys.r = false; syncKeys(); }
    };
    window.addEventListener("keydown", kDown);
    window.addEventListener("keyup", kUp);

    /* ---- dynamics ---- */
    const step = (dt) => {
      if (g.over) return;

      if (g.fallY > 0 || Math.abs(g.ang) > FALL_ANG) {
        // falling into the net
        if (g.fallY === 0) { audio.fall(); }
        g.fallV += 1100 * dt;
        g.fallY += g.fallV * dt;
        g.ang += (g.ang > 0 ? 5 : -5) * dt;
        if (ROPE_Y + g.fallY > 452 && g.netT === 0) {
          g.netT = 0.001;
          audio.boing();
          const secs = Math.floor(g.t);
          setTimeout(() => {
            setFinalTime(secs);
            const p = prizeFor(secs);
            if (p === "pokal") { audio.win(); setClaimed(saveTrophy({ game: "Hangtime" })); }
            else if (p) audio.win();
            g.over = true;
            setPhase("done");
          }, 900);
        }
        if (g.netT > 0) g.netT += dt;
        return;
      }

      g.t += dt;
      const diff = Math.min(1, g.t / 75);
      const K = 2.1 + 2.6 * diff;               // instability grows
      // wind gusts
      g.gustT -= dt;
      if (g.gustT <= 0) {
        g.gustDur = 0.75;
        g.gustAmp = (Math.random() < 0.5 ? -1 : 1) * (0.9 + 2.4 * diff) * (0.6 + Math.random() * 0.7);
        g.gustT = 2.6 - 1.4 * diff + Math.random() * 1.6;
        audio.gust();
        g.popups.push({ x: g.gustAmp > 0 ? 40 : W - 40, y: 180, txt: g.gustAmp > 0 ? "≈≈→" : "←≈≈", c: "#bfe3ff", t: 0 });
      }
      let wind = 0;
      if (g.gustDur > 0) {
        g.gustDur -= dt;
        wind = g.gustAmp * Math.sin(Math.PI * (1 - g.gustDur / 0.75));
      }

      // inverted pendulum (held arrow keys override the pointer/gyro)
      const u = (g.keyInput !== 0 ? g.keyInput * 0.85 : g.input) * U_MAX;
      g.angV += (K * Math.sin(g.ang) + wind + u) * dt;
      g.angV *= 1 - 0.35 * dt;
      g.ang += g.angV * dt;

      // warning beeps near the edge
      if (Math.abs(g.ang) > FALL_ANG * 0.72) {
        g.warnT -= dt;
        if (g.warnT <= 0) { g.warnT = 0.25; audio.warn(); }
      }

      // he walks the rope; each crossing chimes
      const speed = 26 + 10 * diff;
      g.x += g.dir * speed * dt;
      g.walk += speed * dt * 0.22;   // skridt-rytme foelger farten
      if (g.x > W - 70) { g.dir = -1; g.crossings++; audio.cross(); g.popups.push({ x: W / 2, y: 240, txt: t("ht_cross", { n: g.crossings }), c: "#ffd75e", t: 0 }); }
      if (g.x < 70) { g.dir = 1; g.crossings++; audio.cross(); g.popups.push({ x: W / 2, y: 240, txt: t("ht_cross", { n: g.crossings }), c: "#ffd75e", t: 0 }); }

      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1);

      const sec = Math.floor(g.t);
      if (sec !== hudRef.current.time || g.crossings !== hudRef.current.crossings) {
        hudRef.current = { time: sec, crossings: g.crossings };
        setHud(hudRef.current);
      }
    };

    /* ---- drawing (cirkustelt-look som marketing-videoen) ---- */
    const draw = () => {
      // groent teltindre med bloed vignet
      const grad = ctx.createRadialGradient(W / 2, 180, 60, W / 2, 320, 460);
      grad.addColorStop(0, "#2d5334");
      grad.addColorStop(0.6, "#1d3a25");
      grad.addColorStop(1, "#122517");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      // teltduge-striber (svage)
      ctx.fillStyle = "rgba(255,255,255,0.03)";
      for (let x = -40; x < W + 40; x += 46) {
        ctx.beginPath();
        ctx.moveTo(x, 0); ctx.lineTo(x + 23, 0); ctx.lineTo(x + 60, H); ctx.lineTo(x + 37, H);
        ctx.closePath(); ctx.fill();
      }
      // varme spotlys-kegler
      ctx.fillStyle = "rgba(245,201,78,0.08)";
      for (const lx of [60, W - 60]) {
        ctx.beginPath();
        ctx.moveTo(lx, 40); ctx.lineTo(lx - 70, H); ctx.lineTo(lx + 70, H);
        ctx.closePath(); ctx.fill();
      }
      // marquee-paerer langs siderne (som promoens lysrammer)
      for (const bx of [16, W - 16]) {
        for (let by = 40; by < H - 30; by += 42) {
          ctx.beginPath(); ctx.arc(bx, by, 4.5, 0, Math.PI * 2);
          ctx.fillStyle = "#fff3cf"; ctx.fill();
          ctx.beginPath(); ctx.arc(bx, by, 8, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,220,130,0.18)"; ctx.fill();
        }
      }
      // topflag med stjerner (som promoens bunting)
      for (let i = 0; i < 9; i++) {
        const fx = 14 + i * 42;
        ctx.fillStyle = i % 2 ? "#EFE6CE" : "#C8352E";
        ctx.beginPath();
        ctx.moveTo(fx, 4); ctx.lineTo(fx + 30, 4); ctx.lineTo(fx + 15, 32);
        ctx.closePath(); ctx.fill();
        ctx.fillStyle = i % 2 ? "#C8352E" : "#EFE6CE";
        ctx.font = "9px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("★", fx + 15, 17);
      }

      // gauge at the top: needle inside the green zone
      const gx = W / 2, gy = 92, gr = 52;
      ctx.lineWidth = 12;
      const zones = [
        [-1, -0.83, "#d84b4b"], [-0.83, -0.58, "#e8b83a"], [-0.58, 0.58, "#57b06a"],
        [0.58, 0.83, "#e8b83a"], [0.83, 1, "#d84b4b"],
      ];
      for (const [a0, a1, col] of zones) {
        ctx.beginPath();
        ctx.strokeStyle = col;
        ctx.arc(gx, gy, gr, -Math.PI / 2 + a0 * 1.05, -Math.PI / 2 + a1 * 1.05);
        ctx.stroke();
      }
      ctx.lineWidth = 1;
      const na = (g.ang / FALL_ANG) * 1.05;
      ctx.save();
      ctx.translate(gx, gy);
      ctx.rotate(-Math.PI / 2 + Math.max(-1.2, Math.min(1.2, na)));
      ctx.fillStyle = "#f2ead2";
      ctx.beginPath();
      ctx.moveTo(gr - 4, 0); ctx.lineTo(-10, -5); ctx.lineTo(-10, 5);
      ctx.closePath(); ctx.fill();
      ctx.restore();
      ctx.beginPath(); ctx.arc(gx, gy, 6, 0, Math.PI * 2); ctx.fillStyle = "#f5c94e"; ctx.fill();

      // paele + reb (naturreb som promoen)
      ctx.fillStyle = "#6b4a36";
      ctx.fillRect(40, ROPE_Y - 8, 8, 190);
      ctx.fillRect(W - 48, ROPE_Y - 8, 8, 190);
      // linen svajer under bamsen (mest paa midten) og dirrer let i skridtene
      const relx = Math.max(0, Math.min(1, (g.x - 44) / (W - 88)));
      const sag = (5 + 8 * Math.sin(Math.PI * relx)) + Math.sin(g.walk * 6.28) * 0.8;
      const drawRope = (dy, col, lw) => {
        ctx.strokeStyle = col; ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.moveTo(44, ROPE_Y + dy);
        ctx.quadraticCurveTo((44 + g.x) / 2, ROPE_Y + dy + sag * 0.55, g.x, ROPE_Y + dy + sag);
        ctx.quadraticCurveTo((g.x + W - 44) / 2, ROPE_Y + dy + sag * 0.55, W - 44, ROPE_Y + dy);
        ctx.stroke();
      };
      drawRope(0, "#d9c49a", 3.5);
      drawRope(1.5, "rgba(90,60,25,0.45)", 1);

      // TeddyChase.com-lærredsbanner under linen (som i promoen)
      const bw = 232, bh = 34, bx0 = W / 2 - bw / 2, by0 = ROPE_Y + 96;
      ctx.fillStyle = "#f4ecd8";
      ctx.beginPath();
      ctx.moveTo(bx0, by0 + 3);
      ctx.quadraticCurveTo(W / 2, by0 - 4, bx0 + bw, by0 + 3);
      ctx.lineTo(bx0 + bw, by0 + bh);
      ctx.quadraticCurveTo(W / 2, by0 + bh + 7, bx0, by0 + bh);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "rgba(90,60,25,0.35)"; ctx.stroke();
      ctx.fillStyle = "#b3a880";
      for (const rx of [bx0 + 8, bx0 + bw - 8]) {
        ctx.beginPath(); ctx.arc(rx, by0 + 8, 2.2, 0, Math.PI * 2); ctx.fill();
      }
      ctx.fillStyle = "#C8352E";
      ctx.font = "800 19px 'Nunito', system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("TeddyChase.com", W / 2, by0 + 25);   // brandnavn, sprog-invariant

      // sikkerhedsnet i reb-look
      const netY = 470 + (g.netT > 0 ? Math.sin(Math.min(g.netT * 9, Math.PI)) * 16 : 0);
      ctx.strokeStyle = "rgba(201,177,131,0.6)";
      ctx.lineWidth = 1.6;
      for (let i = 0; i < 9; i++) {
        ctx.beginPath(); ctx.moveTo(30 + i * 38, netY); ctx.lineTo(48 + i * 38, netY + 26); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(48 + i * 38, netY); ctx.lineTo(30 + i * 38, netY + 26); ctx.stroke();
      }
      ctx.strokeStyle = "#c9b183"; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(24, netY); ctx.lineTo(W - 24, netY); ctx.stroke();
      ctx.lineWidth = 1;

      // linedanseren er MASKOTTEN (sprite, A-pose) med balancebom over poterne;
      // gang-dut + lille vug goer ham levende paa linen (kun mens han staar paa den)
      const onRope = g.fallY === 0;
      const bob = onRope ? Math.abs(Math.sin(g.walk * 3.14)) * -2.6 : 0;
      const rock = onRope ? Math.sin(g.walk * 3.14) * 0.055 : 0;
      const wy = ROPE_Y + sag + g.fallY + bob;
      ctx.save();
      ctx.translate(g.x, wy);
      ctx.rotate(g.ang + rock);
      if (teddyImg && teddyImg.complete && teddyImg.naturalWidth) {
        const TS = 62;                                   // sprite-hoejde px
        ctx.drawImage(teddyImg, -TS / 2, -TS + 2, TS, TS);
      } else {
        // fallback: pind-figur til spriten er loadet
        ctx.strokeStyle = "#f2ead2"; ctx.lineWidth = 3; ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -26); ctx.stroke();
        ctx.beginPath(); ctx.arc(0, -33, 7, 0, Math.PI * 2);
        ctx.fillStyle = "#f2c9a4"; ctx.fill(); ctx.stroke();
      }
      // balancebom af trae (modvipper let) hen over poterne
      ctx.save();
      ctx.rotate(-g.ang * 0.5 + g.input * 0.22);
      ctx.strokeStyle = "#d9b98c"; ctx.lineWidth = 4.5; ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(-52, -34); ctx.lineTo(52, -34); ctx.stroke();
      ctx.strokeStyle = "rgba(120,85,40,0.5)"; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(-52, -32.5); ctx.lineTo(52, -32.5); ctx.stroke();
      ctx.restore();
      ctx.restore();
      ctx.lineWidth = 1; ctx.lineCap = "butt";

      // popups
      for (const p of g.popups) {
        ctx.globalAlpha = Math.max(0, 1 - p.t);
        ctx.font = "bold 22px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 4; ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 22);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 22);
        ctx.globalAlpha = 1;
      }
    };

    let raf, last = performance.now();
    const loop = (tt) => {
      const dt = Math.min((tt - last) / 1000, 0.033);
      last = tt;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      delete window.__ht;
      window.removeEventListener("resize", fit);
      ro.disconnect();
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("deviceorientation", onOrient);
      window.removeEventListener("keydown", kDown);
      window.removeEventListener("keyup", kUp);
      canvas.removeEventListener("touchstart", tStart);
      canvas.removeEventListener("touchmove", tMove);
      canvas.removeEventListener("touchend", tEnd);
    };
  }, [phase, mode]);
  const hudRef = useRef({ time: 0, crossings: 0 });

  /* ================= UI ================= */
  const prize = prizeFor(finalTime);

  return (
    <div className={"ht-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_hang").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("ht_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("ht_lede")}</p>
          <ul className="rules">
            <li>{t("ht_rule1")}</li>
            <li>{t("ht_rule2")}</li>
            <li>{touch ? t("ht_rule3_touch") : t("ht_rule3_mouse")}</li>
          </ul>
          <p className="prizenote">{t("ht_prizenote", PRIZE)}</p>
          {touch && (
            <>
              <button className="cta" onClick={startGyro}>{t("ht_start_gyro")}</button>
              {gyroErr && <p className="gyroerr">{t("ht_gyro_denied")}</p>}
              <button className="cta alt" onClick={() => startGame("touch")}>{t("ht_start_touch")}</button>
            </>
          )}
          {(!touch || hasFinePointer()) && (
            <button className={touch ? "cta alt" : "cta"} onClick={() => startGame("mouse")}>{t("ht_start_mouse")}</button>
          )}
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{hud.time}s</span>
            <span className="modepill">{mode === "gyro" ? t("ht_mode_gyro") : mode === "mouse" ? t("ht_mode_mouse") : t("ht_mode_touch")}</span>
            <span className="last">{t("ht_crossings", { n: hud.crossings })}</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "done" && prize && prize !== "pokal" && !claimed && (
        <PrizePicker size={prize} game="Hangtime" onDone={(p) => setClaimed(p)} />
      )}

      {phase === "done" && claimed && claimed.kind === "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon gold">{t("ht_trophy_ribbon")}</div>
            <p className="trophybig"><Model3D src="/models/trophy.glb?v=2" thumb="/images/thumbs/trophy.png" fallback="🏆" size={130} /></p>
            <p className="prizeline">{t("ht_trophyline", { s: finalTime })}</p>
            <button className="cta" onClick={() => setPhase("setup")}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "done" && claimed && claimed.kind !== "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("ht_win")}</div>
            <p className="winline">{t("ht_winline", { s: finalTime })}</p>
            <p className="prizeline">
              <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
              <br />
              {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
            </p>
            <button className="cta" onClick={() => setPhase("setup")}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "done" && !prize && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon dark">{t("ht_fail")}</div>
            <p className="winline">{t("ht_failline", { s: finalTime, need: PRIZE.lille })}</p>
            <button className="cta" onClick={() => setPhase("setup")}>{t("try_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.ht-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#141b3a;
  background-image:radial-gradient(circle at 50% -10%, #2a3562 0%, #141b3a 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(24px,7vw,42px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:11.5px; font-weight:800; opacity:.75; line-height:1.4; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.gyroerr{margin:8px 0 0; font-size:12px; font-weight:800; color:#a03028;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.alt{margin-top:10px; background:#2a3562; box-shadow:0 4px 0 #18204a; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #18204a;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.modepill{background:rgba(255,255,255,.12); padding:2px 10px; border-radius:20px; font-size:13px;}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #2a3562; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(8,10,24,.78); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.gold{background:linear-gradient(180deg,#f5c94e,#c8951d); color:#4a3308;}
.ribbon.dark{background:#18204a;}
.winline{font-size:16px; font-weight:800; margin:4px 0 12px;}
.trophybig{margin:6px 0 2px; display:flex; justify-content:center;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
`;
